import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from './auth.service';
import { TABLET_BREAKPOINT, ViewportService } from './viewport.service';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { Subject, of } from 'rxjs';

declare global {
    interface Window { Intercom?: any }
}

const INTERCOM_APP_ID = 'idayx9bm';
const LAUNCHER_PADDING_DEFAULT = 20;
const LAUNCHER_PADDING_ABOVE_TABBAR = 90;

function ensureIntercomScript(appId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        if (typeof window === 'undefined') return resolve();

        if (document.getElementById('intercom-widget-script')) return resolve();

        const s = document.createElement('script');
        s.id = 'intercom-widget-script';
        s.async = true;
        s.src = `https://widget.intercom.io/widget/${appId}`;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Intercom script failed to load'));
        document.head.appendChild(s);
    });
}

function waitForIntercomReady(timeoutMs = 8000): Promise<void> {
    const start = Date.now();
    return new Promise<void>((resolve, reject) => {
        (function tick() {
            if (typeof window !== 'undefined' && typeof window.Intercom === 'function') return resolve();
            if (Date.now() - start > timeoutMs) return reject(new Error('Intercom not ready in time.'));
            requestAnimationFrame(tick);
        })();
    });
}

@Injectable({ providedIn: 'root' })
export class IntercomService implements OnDestroy {
    private destroyed$ = new Subject<void>();
    private booted = false;
    private scriptRequested = false;
    private desiredLauncherVisible = true;
    private launcherPadding = LAUNCHER_PADDING_DEFAULT;

    constructor(
        private auth: AuthService,
        viewport: ViewportService
    ) {
        viewport.state$
            .pipe(
                map((state) => (state.dimensions.width < TABLET_BREAKPOINT ? LAUNCHER_PADDING_ABOVE_TABBAR : LAUNCHER_PADDING_DEFAULT)),
                distinctUntilChanged(),
                takeUntil(this.destroyed$)
            )
            .subscribe((padding) => {
                this.launcherPadding = padding;
                if (typeof window.Intercom === 'function' && this.booted) {
                    window.Intercom('update', { vertical_padding: padding });
                }
            });
    }
    initUserIntercom(): void {
        if (!this.scriptRequested) {
            this.scriptRequested = true;
            ensureIntercomScript(INTERCOM_APP_ID).catch(() => {});
        }

        this.auth.user$
            .pipe(
                filter((u: any) => !!u),
                switchMap(user =>
                    this.auth.getIntercomToken().pipe(
                        switchMap((res: any) => of({ user, jwt: res?.intercomJwt }))
                    )
                ),
                takeUntil(this.destroyed$)
            )
            .subscribe({
                next: async ({ user, jwt }) => {
                    try {
                        await ensureIntercomScript(INTERCOM_APP_ID);
                        await waitForIntercomReady();
                        if (typeof window.Intercom !== 'function') {
                            return;
                        }

                        if (this.booted) {
                            window.Intercom('shutdown');
                            this.booted = false;
                        }

                        window.Intercom('boot', {
                            api_base: 'https://api-iam.intercom.io',
                            app_id: INTERCOM_APP_ID,
                            intercom_user_jwt: jwt,
                            vertical_padding: this.launcherPadding
                        });

                        this.booted = true;
                        this.applyLauncherPreference();

                    } catch {
                    }
                },
                error: () => {}
            });
    }

    setLauncherVisible(visible: boolean): void {
        this.desiredLauncherVisible = visible;

        if (typeof window.Intercom === 'function' && this.booted) {
            window.Intercom('update', { hide_default_launcher: !visible });
        }
    }

    private applyLauncherPreference(): void {
        if (typeof window.Intercom === 'function' && this.booted) {
            window.Intercom('update', { hide_default_launcher: !this.desiredLauncherVisible });
        }
    }

    openMessenger(): void {
        if (typeof window.Intercom === 'function') window.Intercom('show');
    }

    hideMessenger(): void {
        if (typeof window.Intercom === 'function') window.Intercom('hide');
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        if (typeof window.Intercom === 'function') window.Intercom('shutdown');
        this.booted = false;
    }
}
